import PageContainer from '@components/PageContainer';
import { AddressCard, NewAddressButton } from '@components/ui/address';
import { Address, SEAT_CUPRA } from '@components/ui/address/types';
import { Button, Grid, makeStyles, TextField, Typography } from '@material-ui/core';
import { Alert, Autocomplete } from '@material-ui/lab';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useCreateDeliveryAddressForDealer,
  useDeleteDeliveryAddressForDealer,
  useGetDealers,
  useGetDeliveryAddresses,
  useGetDeliveryAddressesForDealer,
  useSetDefaultAddress,
} from 'src/hooks/useDeliveryAddress';
import useRole from 'src/hooks/useRole';
import { useUser } from 'src/hooks/useUser';
import { useWizard } from 'src/hooks/useWizard';
import {
  CountryCode,
  DealerListBasicDTO,
  DeliveryAddressRequest,
  DeliveryAddressType,
  WizardStep,
} from 'src/modules/generated/api';
import UserRole from 'src/types/UserRoles';

const useStyles = makeStyles((theme) => ({
  header: {
    marginBlockEnd: theme.spacing(4.5),
  },
  headline: {
    marginBlockEnd: theme.spacing(2.5),
  },
  addressCard: {
    display: 'grid', // stretch button to match address cards
  },
  dealerSelect: {
    marginBlockEnd: theme.spacing(3),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  dealerSelectInput: {
    width: 300,
  },
}));

const toDeliveryAddressRequest = ({ name, brandOverrides, ...addressFields }: Address): DeliveryAddressRequest => {
  const seatCupra = brandOverrides?.[SEAT_CUPRA];
  // Overrides only exist for Spain; dropping them otherwise clears stale codes after a country change
  const keepSeatCupra =
    seatCupra && addressFields.countryCode === CountryCode.Es && (seatCupra.betriebsId || seatCupra.addressCode);

  return {
    ...addressFields,
    contactPersonName: name,
    countryCode: addressFields.countryCode as CountryCode,
    brandOverrides: keepSeatCupra ? { [SEAT_CUPRA]: seatCupra } : undefined,
  };
};

type DeliveryAddressContainerProps = {
  showInWizard?: boolean;
};

const DeliveryAddressContainer = ({ showInWizard }: DeliveryAddressContainerProps) => {
  const classes = useStyles();
  const { hasRole } = useRole();
  const { t } = useTranslation();
  const isAdmin = hasRole(UserRole.ADMIN);
  const { useTrackWizardConfirmationOnPageView } = useWizard();
  const { data: user } = useUser();
  const ownDealerId = user?.dealer?.id ?? '';

  // pendingDealerId: dealer selected in the autocomplete, not yet confirmed via Load
  // loadedDealerId: dealer whose addresses are currently displayed (confirmed via Load)
  // Both are initialized to the user's own dealer once user data becomes available
  const [pendingDealerId, setPendingDealerId] = useState<string>('');
  const [loadedDealerId, setLoadedDealerId] = useState<string>('');

  useEffect(() => {
    if (ownDealerId && !pendingDealerId) {
      setPendingDealerId(ownDealerId);
      setLoadedDealerId(ownDealerId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownDealerId]);

  // Falls back to ownDealerId before user data resolves (loadedDealerId starts as '')
  const currentDealerId = loadedDealerId || ownDealerId;

  const { data: dealers = [] } = useGetDealers(isAdmin);
  const selectedDealer = useMemo(
    () => dealers.find((d) => d.dealer?.id === (pendingDealerId || ownDealerId)),
    [dealers, pendingDealerId, ownDealerId],
  );

  const { data: addressesForDealer = [], isLoading: loadingDealer } = useGetDeliveryAddressesForDealer(
    currentDealerId,
    isAdmin,
  );
  const { data: addressesForUser = [], isLoading: loadingUser } = useGetDeliveryAddresses(!isAdmin);
  const deliveryAddresses = (isAdmin ? addressesForDealer : addressesForUser).slice().sort((a, b) => {
    if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
    return (a.id ?? '').localeCompare(b.id ?? '');
  });
  const isLoadingAddresses = isAdmin ? loadingDealer : loadingUser;

  const { mutate: createDeliveryAddressForDealer } = useCreateDeliveryAddressForDealer(currentDealerId);
  const { mutate: deleteDeliveryAddressForDealer } = useDeleteDeliveryAddressForDealer(currentDealerId);
  const { mutate: setDefaultAddress } = useSetDefaultAddress(ownDealerId);

  const isOwnDealer = currentDealerId === ownDealerId;

  const handleEdit = (id: string | undefined, updatedAddress: Address) => {
    if (!id) return;
    createDeliveryAddressForDealer({ addressId: id, ...toDeliveryAddressRequest(updatedAddress) });
  };

  useTrackWizardConfirmationOnPageView(WizardStep.DealerAddress, showInWizard);

  return (
    <PageContainer>
      <div className={classes.header}>
        <Typography variant="h1" className={classes.headline}>
          {t('navigation.delivery')}
        </Typography>
        {!isAdmin && (
          <Alert severity="info" variant="outlined">
            <span>
              {t('dealerAdministration.deliveryAddressAdmin')}{' '}
              <a href="mailto:irisscout@porsche.co.at">irisscout@porsche.co.at</a>
            </span>
          </Alert>
        )}
      </div>
      <div>
        {isAdmin && selectedDealer && (
          <div className={classes.dealerSelect}>
            <Typography variant="body2">{t('deliveryAddress.selectDealer')}:</Typography>
            <Autocomplete<DealerListBasicDTO, false, true>
              className={classes.dealerSelectInput}
              disableClearable
              options={dealers}
              getOptionLabel={(option) => [option.dealer?.id, option.name].filter(Boolean).join(' – ')}
              value={selectedDealer}
              onChange={(_event, newValue) => {
                setPendingDealerId(newValue?.dealer?.id ?? '');
              }}
              renderInput={(params) => <TextField {...params} variant="outlined" size="small" />}
            />
            <Button
              variant="contained"
              color="primary"
              disabled={!pendingDealerId || pendingDealerId === loadedDealerId}
              onClick={() => setLoadedDealerId(pendingDealerId)}
            >
              {t('action.load')}
            </Button>
          </div>
        )}
        {!isLoadingAddresses && deliveryAddresses.length === 0 && (
          <Typography variant="body2" gutterBottom>
            {t('deliveryAddress.noAddresses')}
          </Typography>
        )}
        <Grid container spacing={2}>
          {isAdmin ? (
            <Grid item xs={12} md={4} className={classes.addressCard}>
              <NewAddressButton
                onSubmit={(newAddress) => createDeliveryAddressForDealer(toDeliveryAddressRequest(newAddress))}
              />
            </Grid>
          ) : null}
          {deliveryAddresses.map((deliveryAddress) => {
            const isPlatformStandard = deliveryAddress.type === DeliveryAddressType.PlatformStandard;

            return (
              <Grid item xs={12} md={4} className={classes.addressCard} key={deliveryAddress.id}>
                <AddressCard
                  title={t('action.action_noun', {
                    action: t('action.edit'),
                    noun: t('contact.address'),
                  })}
                  name={
                    isPlatformStandard
                      ? t('addressCard.platformStandard.title')
                      : (deliveryAddress.contactPersonName ?? '')
                  }
                  companyName={deliveryAddress.companyName ?? ''}
                  city={deliveryAddress.city ?? ''}
                  street={deliveryAddress.street ?? ''}
                  zip={deliveryAddress.zip ?? ''}
                  country={deliveryAddress.country ?? ''}
                  countryCode={deliveryAddress.countryCode ?? ''}
                  email={deliveryAddress.email ?? ''}
                  phoneNumber={deliveryAddress.phoneNumber ?? ''}
                  faxNumber={deliveryAddress.faxNumber}
                  additionalInformation={deliveryAddress.additionalInformation}
                  betriebsId={deliveryAddress.betriebsId}
                  addressCode={deliveryAddress.addressCode}
                  brandOverrides={deliveryAddress.brandOverrides}
                  isGlobal={deliveryAddress.isDefault}
                  onEdit={
                    isPlatformStandard || !isAdmin ? undefined : (address) => handleEdit(deliveryAddress.id, address)
                  }
                  onDelete={
                    isPlatformStandard || !isAdmin
                      ? undefined
                      : () => deleteDeliveryAddressForDealer(deliveryAddress.id as string)
                  }
                  // Setting the default is available to all users, but only for their own dealer.
                  // Admins can edit/create/delete; non-admins can only set the default.
                  onSetDefault={
                    deliveryAddress.id !== undefined && isOwnDealer
                      ? () => setDefaultAddress(deliveryAddress.id as string)
                      : undefined
                  }
                  content={
                    isPlatformStandard ? (
                      <Alert severity="info">
                        <Typography variant="body2">{t('addressCard.platformStandard.hint')}</Typography>
                      </Alert>
                    ) : null
                  }
                />
              </Grid>
            );
          })}
        </Grid>
      </div>
    </PageContainer>
  );
};

export default DeliveryAddressContainer;
